import React, { useEffect, useRef } from 'react';
import { trackMetaPixel } from '../services/metaPixel';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Grid, Button, Alert, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PetsIcon from '@mui/icons-material/Pets';
import HomeIcon from '@mui/icons-material/Home';

const BookingSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const stateData = location.state || {};
  const bookingDetails = stateData.bookingDetails || JSON.parse(localStorage.getItem('pendingBooking') || '{}');
  const bookingCode = params.get('code') || stateData.bookingCode || bookingDetails.bookingCode || '';
  const amount = Number(params.get('amount')) || stateData.amount || bookingDetails.amount || 0;
  const netAmount = Number(params.get('netAmount')) || stateData.netAmount || bookingDetails.netAmount || 0;
  const authorizationCode = params.get('auth') || stateData.authorizationCode || '';

  const sentRef = useRef(false);

  useEffect(() => {
    localStorage.removeItem('pendingBooking');

    if (!bookingCode || sentRef.current) return;
    sentRef.current = true;

    const ownerName = bookingDetails.ownerName || '';
    const userData = {
      external_id: bookingDetails.ownerRut || '',
      first_name: ownerName.split(' ')[0] || '',
      last_name: ownerName.split(' ').slice(1).join(' ') || '',
      phone: bookingDetails.ownerCelular || '',
      email: bookingDetails.ownerMail || '',
    };

    if (amount > 0) {
      // Pago exitoso con Webpay → evento Purchase con monto NETO
      trackMetaPixel({
        eventName: 'Purchase',
        payload: {
          event_id: `purchase_${bookingCode}`,
          value: netAmount || amount,
          currency: 'CLP',
          content_name: bookingDetails.specialty || 'Agendamiento Online',
          content_category: 'Agendamiento Online',
          content_ids: [bookingCode],
          content_type: 'product',
          order_id: bookingCode,
          ...userData,
        },
      });
    }
    // Nota: la reserva gratuita envía Lead en PublicBooking para evitar duplicados.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatPrice = (val) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(val);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />

          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            ¡Reserva confirmada!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Tu pago fue procesado y tu cita ha sido agendada exitosamente.
          </Typography>

          {bookingCode && (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary">Código de reserva</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: 4, color: 'primary.main', my: 1 }}>
                {bookingCode}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Guarda este código para consultar o cancelar tu cita
              </Typography>
            </Paper>
          )}

          <Grid container spacing={1.5} sx={{ textAlign: 'left', mb: 3 }}>
            {bookingDetails.petName && (
              <Grid size={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <PetsIcon fontSize="small" color="primary" />
                  <Typography variant="body2">
                    <strong>{bookingDetails.petName}</strong>
                    {bookingDetails.specialty ? ` · ${bookingDetails.specialty}` : ''}
                  </Typography>
                </Box>
              </Grid>
            )}
            {bookingDetails.date && (
              <Grid size={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CalendarMonthIcon fontSize="small" color="primary" />
                  <Typography variant="body2">
                    {bookingDetails.date}
                    {bookingDetails.time ? ` · ${bookingDetails.time}` : ''}
                  </Typography>
                </Box>
              </Grid>
            )}
            {bookingDetails.professional && (
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">
                  Dr/a. {bookingDetails.professional}
                  {bookingDetails.branchName ? ` · ${bookingDetails.branchName}` : ''}
                </Typography>
              </Grid>
            )}
            {amount > 0 && (
              <>
                <Grid size={12}><Divider /></Grid>
                <Grid size={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Monto pagado</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatPrice(amount)}
                    </Typography>
                  </Box>
                  {authorizationCode && (
                    <Typography variant="caption" color="text.secondary">
                      Código de autorización: {authorizationCode}
                    </Typography>
                  )}
                </Grid>
              </>
            )}
          </Grid>

          <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
            Te contactaremos al número <strong>{bookingDetails.ownerCelular}</strong> para confirmar los detalles de tu cita.
          </Alert>
          <Alert severity="warning" sx={{ textAlign: 'left', mb: 3 }}>
            El pago de esta reserva <strong>no es reembolsable</strong>. Podrás reagendar tu cita con hasta 3 horas de anticipación llamando al <strong>+56 9 3250 0867</strong>.
          </Alert>

          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/agendar')}
            sx={{ borderRadius: 3, px: 4 }}
          >
            Volver al inicio
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default BookingSuccess;
